import { useEffect, useId, useRef, useState } from 'react';

/* Single-select dropdown with a search box — a drop-in for a native
   <select> whose option list gets too long to scroll through (e.g. the
   topbar Company picker once there are dozens of groups). Same
   { id, label } options shape PortalLayout's groupScope already uses;
   typing filters by label (case-insensitive, so "acme" or a company code
   both match), arrow keys move, Enter picks, Escape / click-outside
   closes. */
function SearchableSelect({ id, className = '', value, onChange, options, disabled, title, placeholder = 'Search…' }) {
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState('');
  const [active, setActive] = useState(0);
  const rootRef = useRef(null);
  const buttonRef = useRef(null);
  const inputRef = useRef(null);
  const listRef = useRef(null);
  const listId = useId();

  const q = query.trim().toLowerCase();
  const filtered = q ? options.filter((o) => o.label.toLowerCase().includes(q)) : options;
  const current = options.find((o) => String(o.id) === String(value));

  // Fresh search each time it opens, with the current choice highlighted.
  useEffect(() => {
    if (!open) return;
    setQuery('');
    setActive(Math.max(0, options.findIndex((o) => String(o.id) === String(value))));
    inputRef.current?.focus();
    // Only on open — not when options/value change underneath it.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  useEffect(() => {
    if (!open) return undefined;
    function onDoc(e) { if (rootRef.current && !rootRef.current.contains(e.target)) setOpen(false); }
    document.addEventListener('mousedown', onDoc);
    return () => document.removeEventListener('mousedown', onDoc);
  }, [open]);

  // Keep the highlighted row in view while arrowing through a long list.
  useEffect(() => {
    if (!open || !listRef.current) return;
    listRef.current.children[active]?.scrollIntoView?.({ block: 'nearest' });
  }, [active, open]);

  function pick(o) {
    setOpen(false);
    buttonRef.current?.focus();
    if (String(o.id) !== String(value)) onChange(o.id);
  }

  function onInputKey(e) {
    if (e.key === 'ArrowDown') { e.preventDefault(); setActive((i) => Math.min(i + 1, filtered.length - 1)); }
    else if (e.key === 'ArrowUp') { e.preventDefault(); setActive((i) => Math.max(i - 1, 0)); }
    else if (e.key === 'Enter') { e.preventDefault(); if (filtered[active]) pick(filtered[active]); }
    else if (e.key === 'Escape') { e.preventDefault(); setOpen(false); buttonRef.current?.focus(); }
    else if (e.key === 'Tab') setOpen(false);
  }

  return (
    <div ref={rootRef} className={'ss ' + className}>
      <button
        ref={buttonRef}
        id={id}
        type="button"
        className="ss-btn"
        disabled={disabled}
        title={title}
        onClick={() => setOpen((o) => !o)}
        onKeyDown={(e) => { if (e.key === 'ArrowDown' && !open) { e.preventDefault(); setOpen(true); } }}
        aria-haspopup="listbox"
        aria-expanded={open}
      >
        <span className="ss-label">{current ? current.label : '—'}</span>
        <span aria-hidden="true" className="ss-caret">▾</span>
      </button>
      {open && (
        <div className="ss-pop">
          <input
            ref={inputRef}
            type="search"
            className="ss-search"
            role="combobox"
            aria-expanded="true"
            aria-controls={listId}
            aria-autocomplete="list"
            aria-activedescendant={filtered[active] ? `${listId}-${active}` : undefined}
            aria-label="Search options"
            placeholder={placeholder}
            value={query}
            onChange={(e) => { setQuery(e.target.value); setActive(0); }}
            onKeyDown={onInputKey}
          />
          <ul ref={listRef} id={listId} role="listbox" className="ss-list">
            {filtered.map((o, i) => (
              <li
                key={o.id}
                id={`${listId}-${i}`}
                role="option"
                aria-selected={String(o.id) === String(value)}
                className={'ss-opt' + (i === active ? ' active' : '') + (String(o.id) === String(value) ? ' sel' : '')}
                onMouseEnter={() => setActive(i)}
                onMouseDown={(e) => e.preventDefault()}
                onClick={() => pick(o)}
              >
                {o.label}
              </li>
            ))}
            {!filtered.length && <li className="ss-empty">No matches</li>}
          </ul>
        </div>
      )}
    </div>
  );
}

export default SearchableSelect;
